/**
 * Global image fade-in.
 *
 * Every <img> in the app starts at opacity:0 (see index.css). We mark it
 * `data-loaded="true"` once the bytes are decoded so CSS fades it in.
 *
 * Handles: cached images, lazy-loaded images, dynamically-mounted images
 * (route changes, carousels, modals, popovers), and broken images.
 */
const ATTR = "data-loaded";

const reveal = (img: HTMLImageElement) => {
  if (img.getAttribute(ATTR) === "true") return;
  img.setAttribute(ATTR, "true");
};

const isReady = (img: HTMLImageElement) =>
  img.complete && img.naturalWidth > 0;

const wire = (img: HTMLImageElement) => {
  if ((img as any).__faderWired) return;
  (img as any).__faderWired = true;

  // Opt-out: above-the-fold images that should never start invisible
  if (img.hasAttribute("data-no-fade")) {
    img.setAttribute(ATTR, "true");
    return;
  }

  if (isReady(img)) {
    reveal(img);
    return;
  }

  const onDone = () => reveal(img);
  img.addEventListener("load", onDone, { once: true });
  img.addEventListener("error", onDone, { once: true });

  // Safety net: some browsers don't reliably fire `load` for images that
  // become complete between wiring and the listener attaching, especially
  // for lazy images that just entered the viewport. Poll briefly.
  let tries = 0;
  const poll = window.setInterval(() => {
    tries += 1;
    if (isReady(img)) {
      reveal(img);
      window.clearInterval(poll);
    } else if (tries > 40) {
      // ~10s, give up polling; the load/error listener will still fire.
      window.clearInterval(poll);
    }
  }, 250);
};

export const initImageFade = () => {
  if (typeof window === "undefined") return;

  const scan = (root: ParentNode) => {
    root.querySelectorAll<HTMLImageElement>("img").forEach(wire);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(document));
  } else {
    scan(document);
  }

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const el = node as Element;
        if (el.tagName === "IMG") wire(el as HTMLImageElement);
        else if (el.querySelectorAll) scan(el);
      });
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Re-scan on route changes / late hydration just in case
  window.addEventListener("load", () => scan(document));
};
